import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/elements/select'
import { SlidePanel } from '@/components/SlidePanel'
import { addEntityRole, deleteEntityRole, updateEntityRole } from '@/services/entity.service'
import {
  addOrganizationRole,
  deleteOrganizationRole,
  getOrganizationAccounts,
  updateOrganizationRole,
} from '@/services/organization.service'
import { ROLE_OPTIONS } from '@/utils/constants'
import { toast } from '@/utils/helpers'
import { formatUserName } from '@/utils/userHelpers'

// Radix Select can't use an empty-string value; this means "no access".
const NO_ROLE = 'none'

// Organizations only have Owner/Admin (no Instructor/Member); one Owner per org.
const ORG_ROLE_OPTIONS = ['Owner', 'Admin']

/**
 * Manage a user's access to an organization from the org entrypoint (RSCX-320):
 * their organization-level role (Owner/Admin) plus their role at each of the
 * org's facilities. Facility changes go through the entity-roles endpoints and
 * org-role changes through the org-roles endpoints — org Owners/Admins are
 * authorized for both. Changes are batched and applied on save.
 */
const OrganizationUserAccessSlideout = ({ user, organizationId, resetAndClose }) => {
  const queryClient = useQueryClient()

  // All facilities in the org (so the user can be added to ones they're not in).
  const { data: accounts } = useQuery({
    queryKey: ['organization-accounts', organizationId, 'all'],
    queryFn: () =>
      getOrganizationAccounts({ organizationId, pagination: { pageSize: 1000, pageIndex: 0 } }),
    placeholderData: keepPreviousData,
  })
  const facilities = accounts?.results ?? []

  // The user's current access: facilityId -> { roleId, role }.
  const current = useMemo(() => {
    const map = {}
    ;(user.facilities ?? []).forEach((facility) => {
      map[facility.entityId] = { roleId: facility.roleId, role: facility.roles?.[0] ?? '' }
    })
    return map
  }, [user])

  // Current org-level role (at most one per user per org).
  const currentOrgRole = user.organizationRoles?.[0] ?? ''
  const orgRoleId = user.organizationRoleId ?? null

  // Editable selections.
  const [orgRole, setOrgRole] = useState(currentOrgRole || NO_ROLE)
  const [selections, setSelections] = useState({})
  const roleFor = (facilityId) => selections[facilityId] ?? current[facilityId]?.role ?? NO_ROLE

  const { isPending, mutate: saveAccess } = useMutation({
    mutationFn: async () => {
      const actions = facilities.flatMap((facility) => {
        const originalRole = current[facility.id]?.role ?? ''
        const selected = roleFor(facility.id)
        const newRole = selected === NO_ROLE ? '' : selected
        if (originalRole === newRole) return []

        if (!originalRole && newRole) {
          return [
            addEntityRole({
              entityId: facility.id,
              roles: [newRole],
              jobs: [],
              email: user.email,
            }),
          ]
        }
        if (originalRole && !newRole) {
          return [deleteEntityRole({ entityId: facility.id, id: current[facility.id].roleId })]
        }
        return [
          updateEntityRole({
            entityId: facility.id,
            id: current[facility.id].roleId,
            roles: [newRole],
          }),
        ]
      })

      // Organization-level role change.
      const newOrgRole = orgRole === NO_ROLE ? '' : orgRole
      if (newOrgRole !== currentOrgRole) {
        if (!currentOrgRole && newOrgRole) {
          actions.push(
            addOrganizationRole({ organizationId, existingUser: user.id, roles: [newOrgRole] }),
          )
        } else if (currentOrgRole && !newOrgRole) {
          actions.push(deleteOrganizationRole({ organizationId, id: orgRoleId }))
        } else {
          actions.push(
            updateOrganizationRole({ organizationId, id: orgRoleId, roles: [newOrgRole] }),
          )
        }
      }

      if (!actions.length) return
      await Promise.all(actions)
    },
    onError: (err) => toast(err.message, 'error'),
    onSuccess: () => {
      toast('Access updated', 'success')
      queryClient.invalidateQueries({ queryKey: ['organization-users', organizationId] })
      queryClient.invalidateQueries({ queryKey: ['organization-roles', organizationId] })
      queryClient.invalidateQueries({ queryKey: ['organization', organizationId, 'summary'] })
      resetAndClose()
    },
  })

  return (
    <SlidePanel
      actions={[
        { label: 'Cancel', onClick: () => resetAndClose(), type: 'cancel' },
        { label: 'Save Access', onClick: () => saveAccess(), type: 'submit' },
      ]}
      content={
        <div className="flex flex-col gap-5">
          <div>
            <p className="text-foreground font-medium">{formatUserName(user)}</p>
            <p className="text-muted-foreground text-sm">{user.email}</p>
          </div>

          <div className="flex flex-col gap-2">
            <span className="leading-6 font-bold">Organization Role</span>
            <p className="text-muted-foreground text-sm">
              Owners and Admins can manage the organization and have full access to every facility.
              Choose <span className="font-medium">No org role</span> to remove them from the
              organization (their facility access below is unaffected).
            </p>
            <Select value={orgRole} onValueChange={setOrgRole} disabled={isPending}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_ROLE}>No org role</SelectItem>
                {ORG_ROLE_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-3">
            <span className="leading-6 font-bold">Facility Access</span>
            <p className="text-muted-foreground text-sm">
              Set this user&apos;s role at each facility in the organization. Choosing{' '}
              <span className="font-medium">No access</span> removes them from that facility.
            </p>
            {facilities.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                This organization has no facilities yet.
              </p>
            ) : (
              facilities.map((facility) => (
                <div key={facility.id} className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium">{facility.name}</span>
                  <Select
                    value={roleFor(facility.id)}
                    onValueChange={(value) =>
                      setSelections((prev) => ({ ...prev, [facility.id]: value }))
                    }
                    disabled={isPending}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_ROLE}>No access</SelectItem>
                      {ROLE_OPTIONS.map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))
            )}
          </div>
        </div>
      }
      loading={isPending}
      title="Manage Access"
      setOpen={resetAndClose}
    />
  )
}

OrganizationUserAccessSlideout.propTypes = {
  user: PropTypes.object.isRequired,
  organizationId: PropTypes.string.isRequired,
  resetAndClose: PropTypes.func.isRequired,
}

export default OrganizationUserAccessSlideout
